import PropTypes from "prop-types";
import React, { useRef } from "react";
import { connect } from "react-redux";
import selectors from "selectors";
import { useHistory } from "react-router-dom";
import { Formik } from "formik";
import * as Yup from "yup";
import { TextField, Button } from "@mui/material";
import { forgotPassword } from "actions/auth";
import { Spinner } from "components/ui";
import { translateUsingBrowser } from "utils/misc";
import useDocumentTitle from "hooks/use-document-title";

export const ForgotPassword = ({ forgotPassword, forgotPasswordError }) => {
  const history = useHistory();
  const formikRef = useRef(null);

  useDocumentTitle(translateUsingBrowser("forgotPassword"));

  const getFieldError = (field, errors, touched) => {
    return touched[field] ? errors[field] : "";
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formikRef.current) return;

    const errors = await formikRef.current.validateForm();
    formikRef.current.setTouched(
      Object.keys(errors).reduce((acc, key) => ({ ...acc, [key]: true }), {})
    );

    if (Object.keys(errors).length > 0) {
      // Focus the first field with an error
      const firstErrorField = Object.keys(errors)[0];
      const element = document.getElementById(firstErrorField);
      if (element) {
        element.focus();
      }
      return;
    }

    formikRef.current.handleSubmit();
  };

  return (
    <div className="login-screen" role="main">
      <div className="login-background" aria-hidden="true">
        <div className="white-background"></div>
        <div className="color-background"></div>
      </div>

      <header className="login-screen-header">
        <div className="login-logo" role="img" aria-label="My Sleep Coach Powered by WellSky" />
      </header>

      <div className="login-container">
        <div className="login-form-card" role="form">
          <h1 className="login-card-header" id="forgot-password-title">
            {translateUsingBrowser("forgotPassword")}
          </h1>
          <Formik
            innerRef={formikRef}
            initialValues={{ email: "" }}
            validationSchema={Yup.object().shape({
              email: Yup.string()
                .required(translateUsingBrowser("emailIsRequired"))
                .email(translateUsingBrowser("enterValidEmail"))
            })}
            onSubmit={async ({ email }) => {
              await forgotPassword({ email });
            }}
          >
            {({
              isSubmitting,
              handleChange,
              errors,
              touched,
              handleBlur,
              values
            }) => (
              <form onSubmit={handleFormSubmit} aria-labelledby="forgot-password-title">
                <div className="form-field">
                  <TextField
                    id="email"
                    label={translateUsingBrowser("email")}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.email}
                    name="email"
                    error={!!getFieldError("email", errors, touched)}
                    helperText={getFieldError("email", errors, touched)}
                    type="email"
                    autoComplete="email"
                    placeholder={translateUsingBrowser("enterEmail")}
                    slotProps={{
                      inputLabel: {
                        shrink: true
                      }
                    }}
                  />
                </div>
                <div className="login-info" role="status" aria-live="polite">
                  {forgotPasswordError && (
                    <div className="login-error" role="alert">
                      {forgotPasswordError}
                    </div>
                  )}
                </div>
                {isSubmitting ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minHeight: 120
                    }}
                    role="status"
                    aria-label="Sending reset email"
                  >
                    <Spinner />
                  </div>
                ) : (
                  <div className="login-form-actions">
                    <Button
                      variant="contained"
                      color="primary"
                      id="login-button"
                      type="submit"
                    >
                      {translateUsingBrowser("sendResetPasswordEmail")}
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      type="button"
                      onClick={() => {
                        history.push("/login");
                      }}
                    >
                      {translateUsingBrowser("backToLogin")}
                    </Button>
                  </div>
                )}
              </form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default connect(
  state => ({ forgotPasswordError: selectors.getForgotPasswordError(state) }),
  {
    forgotPassword
  }
)(ForgotPassword);

ForgotPassword.propTypes = {
  forgotPassword: PropTypes.func.isRequired,
  forgotPasswordError: PropTypes.string
};
